import { useContext } from "react";
import { Box } from "@mui/material";

import { FormLabel } from "~/components/design-system/FormLabel.js";
import { HelperText } from "~/components/design-system/HelperText/HelperText.js";
import { Typeahead } from "~/components/design-system/Typeahead/Typeahead.js";
import { ManagedLocationsContext } from "~/Contexts.js";

export type SelectedLocalAuthority = {
  /** Location ID - only present when a LA is matched from the list */
  id?: string;
  /** The displayed/typed name - always present */
  name: string;
} | null;

interface LocalAuthorityPickerProps {
  value: SelectedLocalAuthority;
  onChange: (localAuthority: SelectedLocalAuthority) => void;
  label?: string;
  ariaLabel?: string;
  error?: string;
}

export function LocalAuthorityPicker({
  value,
  onChange,
  label = "Local Authority",
  ariaLabel,
  error,
}: LocalAuthorityPickerProps) {
  const managedLocationsContext = useContext(ManagedLocationsContext);

  const localAuthorities = managedLocationsContext.managedLocations
    .filter((location) => location.type === "localAuthority")
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleChange = (val: string | null) => {
    if (!val) {
      onChange(null);
      return;
    }

    const la = localAuthorities.find((location) => location.name === val);

    if (la) {
      onChange({
        id: la.id,
        name: la.name,
      });
    } else {
      // Partial/free text entry - store the typed value but no id
      onChange({
        id: undefined,
        name: val,
      });
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      {!!label && <FormLabel label={label} />}
      <Typeahead
        placeholder="Search local authorities..."
        enableFuse={false}
        options={localAuthorities}
        inputValue={value?.name || ""}
        optionsKey="name"
        name="local-authority"
        inputProps={{
          "aria-label": ariaLabel || label || "Local Authority",
        }}
        getOptionLabel={(la: any) => {
          if (typeof la === "string") {
            return la;
          } else {
            return la.name;
          }
        }}
        onInputChange={handleChange}
      />
      {!!error && <HelperText errorMessage={error} />}
    </Box>
  );
}
